/* Sparposten Theme V22: Varianten immer kaufbar, keine lagerbasierte Mengenbegrenzung, Status-Badge im Vordergrund. */
(() => {
  'use strict';

  document.documentElement.classList.remove('no-js');

  const shopRoot = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const currency = () => document.body?.dataset.currency || window.Shopify?.currency?.active || 'CHF';

  const formatMoney = (amount, currencyCode = currency()) => {
    const locale = document.documentElement.lang || 'de-CH';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency: currencyCode }).format(amount / 100);
    } catch (_error) {
      return `${(amount / 100).toFixed(2)} ${currencyCode}`;
    }
  };

  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    "'": '&#39;',
    '"': '&quot;',
  }[character]));

  const updateScrollLock = () => {
    const navigationOpen = document.querySelector('#MobileDrawer.is-open');
    const cartOpen = document.querySelector('[data-cart-drawer-root].is-open');
    document.body.style.overflow = navigationOpen || cartOpen ? 'hidden' : '';
  };

  const closeMegaMenus = (except = null) => {
    document.querySelectorAll('.sp-mega-item.is-open').forEach((item) => {
      if (item === except) return;
      item.classList.remove('is-open');
      item.querySelector('[data-mega-toggle]')?.setAttribute('aria-expanded', 'false');
    });
  };

  const closeMobileDrawer = () => {
    const drawer = document.querySelector('#MobileDrawer');
    drawer?.classList.remove('is-open');
    document.querySelectorAll('[data-drawer-open]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
    updateScrollLock();
  };

  const normaliseQuantity = (input, value) => {
    const min = Number.parseFloat(input.min) || 1;
    const safeValue = Number.isFinite(value) ? value : min;
    return Math.max(min, safeValue);
  };

  const changeQuantity = (button) => {
    const control = button.closest('[data-quantity-control]');
    const input = control?.querySelector('[data-quantity-input]');
    if (!input) return;

    const step = Number.parseFloat(input.step) || 1;
    const current = Number.parseFloat(input.value) || Number.parseFloat(input.min) || 1;
    const direction = button.matches('[data-quantity-plus]') ? 1 : -1;
    input.value = String(normaliseQuantity(input, current + (direction * step)));
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const refreshCartSummary = async (existingCart = null) => {
    let cart = existingCart;
    if (!cart) {
      const response = await fetch(`${shopRoot()}cart.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error('Warenkorb konnte nicht geladen werden.');
      cart = await response.json();
    }

    document.querySelectorAll('[data-cart-count]').forEach((count) => {
      count.textContent = String(cart.item_count);
      count.hidden = cart.item_count === 0;
    });
    document.querySelectorAll('[data-cart-total]').forEach((total) => {
      total.textContent = formatMoney(cart.total_price, cart.currency || currency());
    });
    document.querySelectorAll('.sp-cart').forEach((cartLink) => {
      cartLink.setAttribute('aria-label', `Warenkorb, ${cart.item_count} Artikel`);
    });

    return cart;
  };

  let cartDrawerReturnFocus = null;

  const renderCartDrawer = (cart) => {
    const root = document.querySelector('[data-cart-drawer-root]');
    const content = root?.querySelector('[data-cart-drawer-content]');
    const footer = root?.querySelector('[data-cart-drawer-footer]');
    const total = root?.querySelector('[data-cart-drawer-total]');
    if (!content || !footer || !total) return;

    total.textContent = formatMoney(cart.total_price, cart.currency || currency());

    if (!cart.items?.length) {
      content.innerHTML = `
        <div class="sp-cart-drawer__empty">
          <span aria-hidden="true">🛒</span>
          <h3>Dein Warenkorb ist leer</h3>
          <p>Entdecke jetzt unsere aktuellen Sparposten.</p>
          <button class="sp-button" type="button" data-cart-drawer-close>Weiter einkaufen</button>
        </div>`;
      footer.hidden = true;
      return;
    }

    content.innerHTML = `<div class="sp-cart-drawer__items">${cart.items.map((item) => {
      const variantTitle = item.variant_title && item.variant_title !== 'Default Title'
        ? `<div class="sp-cart-item__variant">${escapeHtml(item.variant_title)}</div>`
        : '';
      const image = item.image
        ? `<a class="sp-cart-item__media" href="${escapeHtml(item.url)}"><img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.product_title)}" width="120" height="120" loading="lazy"></a>`
        : '<div class="sp-cart-item__media sp-cart-item__media--empty" aria-hidden="true">📦</div>';

      return `
        <article class="sp-cart-item" data-cart-line="${escapeHtml(item.key)}">
          ${image}
          <div class="sp-cart-item__info">
            <a class="sp-cart-item__title" href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>
            ${variantTitle}
            <div class="sp-cart-item__price">${formatMoney(item.final_line_price, cart.currency || currency())}</div>
            <div class="sp-cart-item__actions">
              <div class="sp-cart-item__quantity" aria-label="Menge ändern">
                <button type="button" data-cart-line-change data-line-key="${escapeHtml(item.key)}" data-quantity="${Math.max(0, item.quantity - 1)}" aria-label="Menge verringern">−</button>
                <span aria-label="Menge">${item.quantity}</span>
                <button type="button" data-cart-line-change data-line-key="${escapeHtml(item.key)}" data-quantity="${item.quantity + 1}" aria-label="Menge erhöhen">+</button>
              </div>
              <button class="sp-cart-item__remove" type="button" data-cart-line-change data-line-key="${escapeHtml(item.key)}" data-quantity="0">Entfernen</button>
            </div>
          </div>
        </article>`;
    }).join('')}</div>`;
    footer.hidden = false;
  };

  const loadCartDrawer = async (existingCart = null) => {
    const content = document.querySelector('[data-cart-drawer-content]');
    if (content && !existingCart) content.innerHTML = '<div class="sp-cart-drawer__loading">Warenkorb wird geladen …</div>';
    const cart = await refreshCartSummary(existingCart);
    renderCartDrawer(cart);
    return cart;
  };

  const openCartDrawer = async (trigger = null, existingCart = null) => {
    const root = document.querySelector('[data-cart-drawer-root]');
    const drawer = root?.querySelector('[data-cart-drawer]');
    if (!root || !drawer) return;

    cartDrawerReturnFocus = trigger || document.activeElement;
    closeMobileDrawer();
    closeMegaMenus();
    root.classList.add('is-open');
    root.setAttribute('aria-hidden', 'false');
    updateScrollLock();

    try {
      await loadCartDrawer(existingCart);
    } catch (_error) {
      const content = root.querySelector('[data-cart-drawer-content]');
      if (content) content.innerHTML = '<div class="sp-cart-drawer__error">Der Warenkorb konnte nicht geladen werden. Bitte öffne die Warenkorbseite.</div>';
    }

    root.querySelector('.sp-cart-drawer__close')?.focus();
  };

  const closeCartDrawer = () => {
    const root = document.querySelector('[data-cart-drawer-root]');
    if (!root?.classList.contains('is-open')) return;
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    updateScrollLock();
    if (cartDrawerReturnFocus instanceof HTMLElement) cartDrawerReturnFocus.focus();
    cartDrawerReturnFocus = null;
  };

  const changeCartLine = async (button) => {
    const key = button.dataset.lineKey;
    const quantity = Number.parseInt(button.dataset.quantity || '0', 10);
    if (!key || !Number.isFinite(quantity)) return;

    button.disabled = true;
    const drawer = document.querySelector('[data-cart-drawer]');
    drawer?.classList.add('is-loading');

    try {
      const response = await fetch(`${shopRoot()}cart/change.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      const cart = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(cart.description || 'Menge konnte nicht geändert werden.');
      await refreshCartSummary(cart);
      renderCartDrawer(cart);
    } catch (_error) {
      await loadCartDrawer().catch(() => {});
    } finally {
      drawer?.classList.remove('is-loading');
    }
  };

  const addItemToCart = async (variantId, quantity) => {
    const response = await fetch(`${shopRoot()}cart/add.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity }] }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.description || payload.message || 'Das Produkt konnte nicht hinzugefügt werden.');
    return payload;
  };

  const updateCardVariant = (select) => {
    const card = select.closest('[data-product-card]');
    const option = select.selectedOptions[0];
    const button = card?.querySelector('[data-card-add]');
    const quantity = card?.querySelector('[data-quantity-input]');
    const price = card?.querySelector('[data-card-price]');
    const compare = card?.querySelector('[data-card-compare]');
    const badge = card?.querySelector('[data-card-badge]');
    if (!card || !option || !button) return;

    const optionPrice = Number.parseInt(option.dataset.price || '0', 10);
    const compareAtPrice = Number.parseInt(option.dataset.compareAtPrice || '0', 10);
    button.dataset.variantId = option.value;
    if (!button.hasAttribute('aria-busy')) {
      button.disabled = false;
      button.textContent = button.dataset.defaultLabel || 'In den Warenkorb';
    }
    if (price) price.textContent = formatMoney(optionPrice);
    if (compare) {
      compare.textContent = compareAtPrice > optionPrice ? formatMoney(compareAtPrice) : '';
      compare.hidden = compareAtPrice <= optionPrice;
    }
    if (badge) {
      if (compareAtPrice > optionPrice && compareAtPrice > 0) {
        const discount = Math.round(((compareAtPrice - optionPrice) * 100) / compareAtPrice);
        badge.textContent = `-${discount}%`;
        badge.hidden = false;
      } else {
        badge.textContent = '';
        badge.hidden = true;
      }
    }
    if (quantity) quantity.value = String(normaliseQuantity(quantity, Number.parseFloat(quantity.value)));
  };

  const updateProductVariant = (select) => {
    const root = select.closest('[data-product-root]');
    const option = select.selectedOptions[0];
    if (!root || !option) return;

    const optionPrice = Number.parseInt(option.dataset.price || '0', 10);
    const compareAtPrice = Number.parseInt(option.dataset.compareAtPrice || '0', 10);
    const price = root.querySelector('[data-product-price]');
    const compare = root.querySelector('[data-product-compare]');
    const quantity = root.querySelector('[data-quantity-input]');
    const addButton = root.querySelector('[data-product-add]');
    const status = root.querySelector('[data-product-status]');

    if (price) price.textContent = formatMoney(optionPrice);
    if (compare) {
      compare.textContent = compareAtPrice > optionPrice ? `statt ${formatMoney(compareAtPrice)}` : '';
      compare.hidden = compareAtPrice <= optionPrice;
    }
    if (quantity) quantity.value = String(normaliseQuantity(quantity, Number.parseFloat(quantity.value)));
    if (addButton && !addButton.hasAttribute('aria-busy')) {
      addButton.disabled = false;
      addButton.textContent = addButton.dataset.defaultLabel || 'In den Warenkorb';
    }
    if (status) status.textContent = '';

    const imageId = option.dataset.imageId;
    if (imageId) root.querySelector(`[data-product-gallery-thumb][data-media-id="${imageId}"]`)?.click();

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', option.value);
      window.history.replaceState({}, '', url);
    } catch (_error) {
      // URL-Aktualisierung ist nur eine Komfortfunktion.
    }
  };

  const addCardToCart = async (button) => {
    const card = button.closest('[data-product-card]');
    const input = card?.querySelector('[data-quantity-input]');
    const message = card?.querySelector('[data-card-message]');
    const variantId = Number.parseInt(button.dataset.variantId || '', 10);
    const quantity = input ? normaliseQuantity(input, Number.parseInt(input.value || '1', 10)) : 1;
    const defaultLabel = button.dataset.defaultLabel || 'In den Warenkorb';

    if (!variantId || button.disabled) return;
    if (input) input.value = String(quantity);

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = 'Wird hinzugefügt …';
    if (message) {
      message.textContent = '';
      message.classList.remove('is-error', 'is-success');
    }

    try {
      await addItemToCart(variantId, quantity);
      const cart = await refreshCartSummary();
      button.textContent = 'Hinzugefügt ✓';
      if (message) {
        message.textContent = 'Im Warenkorb.';
        message.classList.add('is-success');
      }
      await openCartDrawer(button, cart);

      window.setTimeout(() => {
        if (!button.isConnected) return;
        const select = card?.querySelector('[data-card-variant]');
        if (select) updateCardVariant(select);
        else button.textContent = defaultLabel;
        if (message) message.textContent = '';
      }, 1800);
    } catch (error) {
      button.textContent = 'Nochmals versuchen';
      if (message) {
        message.textContent = error instanceof Error ? error.message : 'Das Produkt konnte nicht hinzugefügt werden.';
        message.classList.add('is-error');
      }
      window.setTimeout(() => {
        if (button.isConnected) button.textContent = defaultLabel;
      }, 2800);
    } finally {
      button.removeAttribute('aria-busy');
      const select = card?.querySelector('[data-card-variant]');
      button.disabled = false;
    }
  };

  const submitProductForm = async (form) => {
    const addButton = form.querySelector('[data-product-add]');
    const status = form.querySelector('[data-product-status]');
    const formData = new FormData(form);
    const variantId = Number.parseInt(String(formData.get('id') || ''), 10);
    const quantity = Number.parseInt(String(formData.get('quantity') || '1'), 10);
    if (!variantId || addButton?.disabled) return;

    if (addButton) {
      addButton.disabled = true;
      addButton.setAttribute('aria-busy', 'true');
      addButton.textContent = 'Wird hinzugefügt …';
    }
    if (status) {
      status.textContent = '';
      status.classList.remove('is-error', 'is-success');
    }

    try {
      await addItemToCart(variantId, quantity);
      const cart = await refreshCartSummary();
      if (addButton) addButton.textContent = 'Hinzugefügt ✓';
      if (status) {
        status.textContent = 'Das Produkt wurde in den Warenkorb gelegt.';
        status.classList.add('is-success');
      }
      await openCartDrawer(addButton, cart);
    } catch (error) {
      if (addButton) addButton.textContent = 'Nochmals versuchen';
      if (status) {
        status.textContent = error instanceof Error ? error.message : 'Das Produkt konnte nicht hinzugefügt werden.';
        status.classList.add('is-error');
      }
    } finally {
      window.setTimeout(() => {
        if (!addButton?.isConnected) return;
        addButton.removeAttribute('aria-busy');
        addButton.disabled = false;
        addButton.textContent = addButton.dataset.defaultLabel || 'In den Warenkorb';
        const select = form.querySelector('[data-product-variant-select]');
        if (select) updateProductVariant(select);
      }, 1600);
    }
  };

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const cartDrawerOpen = event.target.closest('[data-cart-drawer-open]');
    if (cartDrawerOpen) {
      event.preventDefault();
      openCartDrawer(cartDrawerOpen);
      return;
    }

    if (event.target.closest('[data-cart-drawer-close]')) {
      event.preventDefault();
      closeCartDrawer();
      return;
    }

    const cartLineButton = event.target.closest('[data-cart-line-change]');
    if (cartLineButton) {
      event.preventDefault();
      changeCartLine(cartLineButton);
      return;
    }

    const quantityButton = event.target.closest('[data-quantity-minus], [data-quantity-plus]');
    if (quantityButton) {
      event.preventDefault();
      changeQuantity(quantityButton);
      return;
    }

    const cardAddButton = event.target.closest('[data-card-add]');
    if (cardAddButton) {
      event.preventDefault();
      addCardToCart(cardAddButton);
      return;
    }

    const megaToggle = event.target.closest('[data-mega-toggle]');
    if (megaToggle) {
      event.preventDefault();
      const item = megaToggle.closest('.sp-mega-item');
      const willOpen = !item?.classList.contains('is-open');
      closeMegaMenus(item);
      item?.classList.toggle('is-open', willOpen);
      megaToggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      return;
    }

    const drawerOpen = event.target.closest('[data-drawer-open]');
    if (drawerOpen) {
      const drawer = document.querySelector('#MobileDrawer');
      drawer?.classList.add('is-open');
      drawerOpen.setAttribute('aria-expanded', 'true');
      updateScrollLock();
      return;
    }

    if (event.target.closest('[data-drawer-close]')) {
      closeMobileDrawer();
      return;
    }

    if (!event.target.closest('.sp-mega-item')) closeMegaMenus();
  });

  document.addEventListener('change', (event) => {
    if (!(event.target instanceof Element)) return;

    const cardVariant = event.target.closest('[data-card-variant]');
    if (cardVariant) {
      updateCardVariant(cardVariant);
      return;
    }

    const productVariant = event.target.closest('[data-product-variant-select]');
    if (productVariant) {
      updateProductVariant(productVariant);
      return;
    }

    const input = event.target.closest('[data-quantity-input]');
    if (!input) return;
    input.value = String(normaliseQuantity(input, Number.parseFloat(input.value)));
  });

  document.addEventListener('submit', (event) => {
    if (!(event.target instanceof HTMLFormElement)) return;
    const productForm = event.target.closest('[data-product-form]');
    if (!productForm) return;
    event.preventDefault();
    submitProductForm(productForm);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    closeMegaMenus();
    closeMobileDrawer();
    closeCartDrawer();
  });

  document.querySelectorAll('[data-card-variant]').forEach(updateCardVariant);
  document.querySelectorAll('[data-product-variant-select]').forEach(updateProductVariant);
})();
